import type { StaffRole } from "@lafayette/auth";
import { useMemo } from "react";

import { type CapabilityRules, capabilityFlags } from "../access/capabilities";
import { useStaffRoles } from "../access/use-staff-roles";

// What a staff member may do in the team, coverage and support screens. Presentation only: the API decides every request again. The roles come
// from the domain's `capabilityRoles`.

export type TeamCapabilities = Readonly<{
  manageRoles: boolean;
  manageStatus: boolean;
  manageCoverage: boolean;
  readCustomers: boolean;
}>;

const teamCapabilityRules = {
  manageRoles: "staff.manage_roles",
  manageStatus: "staff.manage_status",
  manageCoverage: "assignment.manage",
  readCustomers: ["customer.read_all", "customer.read_assigned"],
} as const satisfies CapabilityRules;

export function teamCapabilitiesForRoles(roles: readonly StaffRole[]): TeamCapabilities {
  return capabilityFlags(teamCapabilityRules, roles);
}

export function useTeamCapabilities(): Readonly<{
  capabilities: TeamCapabilities;
  isLoading: boolean;
}> {
  const { roles, isLoading } = useStaffRoles();
  const capabilities = useMemo(() => teamCapabilitiesForRoles(roles), [roles]);
  return { capabilities, isLoading };
}
